import { useEffect, useState } from "react";
import {
  Euro,
  CalendarDays,
  ShoppingBag,
  Ticket,
  Gift,
  UtensilsCrossed,
  PackageX,
} from "lucide-react";
import api from "@/lib/api";
import AnalyticsPanel from "./AnalyticsPanel";

const TYPE_LABELS = {
  reservation: "Acomptes réservation",
  order: "Commandes à emporter",
  shop_order: "Le Comptoir",
  gift_card: "Cartes cadeaux",
  event_ticket: "Billetterie",
  service_booking: "Maasaï Services",
};

const fmt = (n) => String((n || 0).toFixed(2)).replace(".", ",") + " €";

export default function DashboardManager() {
  const [stats, setStats] = useState(null);
  const [stockAlerts, setStockAlerts] = useState([]);

  useEffect(() => {
    api
      .get("/admin/stats")
      .then((r) => setStats(r.data))
      .catch(() => setStats({}));
    api
      .get("/admin/stock-alerts")
      .then((r) => setStockAlerts(r.data))
      .catch(() => {});
  }, []);

  if (!stats)
    return <p className="text-sm text-ivory/40">Chargement des statistiques…</p>;

  const cards = [
    { icon: Euro, label: "Chiffre d'affaires encaissé", value: fmt(stats.revenue_total), testId: "stat-revenue" },
    { icon: CalendarDays, label: "Réservations confirmées", value: stats.reservations_confirmed, testId: "stat-reservations" },
    { icon: ShoppingBag, label: "Commandes payées", value: stats.orders_paid, testId: "stat-orders" },
    { icon: Ticket, label: "Places vendues", value: stats.tickets_sold, testId: "stat-tickets" },
    { icon: Gift, label: "Cartes cadeaux vendues", value: stats.gift_cards_sold, testId: "stat-giftcards" },
    { icon: UtensilsCrossed, label: "Plats à la carte", value: stats.menu_items, testId: "stat-menu" },
  ];

  return (
    <div data-testid="dashboard-manager">
      <h2 className="mb-8 text-lg font-semibold text-ivory">Tableau de bord</h2>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="stats-cards">
        {cards.map((c) => (
          <div
            key={c.label}
            className="rounded-lg border border-white/10 bg-coal/60 p-6"
            data-testid={c.testId}
          >
            <c.icon className="mb-4 h-5 w-5 text-copper" strokeWidth={1.5} />
            <p className="text-2xl font-bold tracking-tight text-ivory">{c.value}</p>
            <p className="mt-1 text-xs uppercase tracking-[0.15em] text-ivory/40">{c.label}</p>
          </div>
        ))}
      </div>

      {stockAlerts.length > 0 && (
        <div className="mt-6 rounded-lg border border-copper/40 bg-copper/10 p-6" data-testid="stock-alerts-panel">
          <h3 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.2em] text-copper">
            <PackageX className="h-4 w-4" strokeWidth={1.5} />
            Alertes stock — {stockAlerts.length} produit(s) à réapprovisionner
          </h3>
          <ul className="mt-4 space-y-2">
            {stockAlerts.map((p) => (
              <li key={p.id} className="flex items-center justify-between text-sm" data-testid={`stock-alert-${p.id}`}>
                <span className="text-ivory/80">{p.name}</span>
                <span className={`text-xs font-semibold ${p.stock <= 0 ? "text-red-400" : "text-copper"}`}>
                  {p.stock <= 0 ? "Rupture" : `${p.stock} restant(s) (seuil : ${p.alert_threshold})`}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-10 grid gap-6 lg:grid-cols-2">
        <div className="rounded-lg border border-white/10 bg-coal/60 p-6" data-testid="stats-revenue-by-type">
          <h3 className="mb-5 text-sm font-semibold uppercase tracking-[0.2em] text-gold">
            Revenus par activité
          </h3>
          {Object.keys(stats.revenue_by_type || {}).length === 0 ? (
            <p className="text-sm text-ivory/40">Aucun paiement encaissé pour le moment.</p>
          ) : (
            <ul className="space-y-3">
              {Object.entries(stats.revenue_by_type).map(([type, amount]) => (
                <li key={type} className="flex items-center justify-between text-sm">
                  <span className="text-ivory/70">{TYPE_LABELS[type] || type}</span>
                  <span className="font-semibold text-gold">{fmt(amount)}</span>
                </li>
              ))}
            </ul>
          )}
          {stats.reservations_pending > 0 && (
            <p className="mt-5 border-t border-white/5 pt-4 text-xs text-ivory/40">
              {stats.reservations_pending} réservation(s) en attente de paiement.
            </p>
          )}
        </div>

        <div className="rounded-lg border border-white/10 bg-coal/60 p-6" data-testid="stats-recent">
          <h3 className="mb-5 text-sm font-semibold uppercase tracking-[0.2em] text-gold">
            Activité récente
          </h3>
          {stats.recent_reservations?.length === 0 && stats.recent_orders?.length === 0 ? (
            <p className="text-sm text-ivory/40">Aucune activité pour le moment.</p>
          ) : (
            <ul className="space-y-3">
              {stats.recent_reservations?.map((r) => (
                <li key={r.id} className="flex items-center justify-between gap-3 text-sm">
                  <span className="min-w-0 truncate text-ivory/70">
                    Réservation — {r.name} · {r.date} {r.time}
                  </span>
                  <span className={`shrink-0 text-xs ${r.status === "confirmee" ? "text-gold" : "text-ivory/35"}`}>
                    {r.status === "confirmee" ? "Confirmée" : r.status === "annulee" ? "Annulée" : "En attente"}
                  </span>
                </li>
              ))}
              {stats.recent_orders?.map((o) => (
                <li key={o.id} className="flex items-center justify-between gap-3 text-sm">
                  <span className="min-w-0 truncate text-ivory/70">
                    Commande {(o.source || "emporter") === "comptoir" ? "Comptoir" : "à emporter"} — {o.name}
                  </span>
                  <span className="shrink-0 text-xs text-gold">{fmt(o.total)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <AnalyticsPanel />
    </div>
  );
}
